import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  AfterViewInit,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
} from "@angular/core";
import {
  GridOptions,
  GridReadyEvent,
  GridApi,
  ColumnResizedEvent,
  RowNode,
  Constants
} from "ag-grid-community";
import "ag-grid-enterprise";
import { CustomHeaderComponent } from "./custom-header/custom-header.component";
import { SpanCellRendererComponent } from "./span/span-cell-renderer.component";
import { LinkCellRendererComponent } from "./link/link-cell-renderer.component";
import { CustomFilterComponent } from "./custom-filter/custom-filter.component";
import {
  BsshDataTableApiService,
  DataLoadingStatus,
} from "./services/bssh-data-table-api-service.service";
import { BsshDataTableServerSideDatasourceService } from "./services/bssh-data-table-server-side-datasource.service";
import { PaginationComponent } from "@bssh/comp-lib";
import { CustomCellLoadingRendererComponent } from "./custom-cell-loading-renderer/custom-cell-loading-renderer.component";
import { CustomLoadingOverlayComponent } from "./custom-loading-overlay/custom-loading-overlay.component";
import { IServerSideRequestOptions } from "./data-source.interfaces";
import { defaultGridOptions } from "./default-gridOptions";
import { SubSink } from "subsink";
import debounce from "lodash/debounce";
import { CustomPageSizeControllerComponent } from "./custom-page-size-controller/custom-page-size-controller.component";
import { PopoverCellRendererComponent } from "./popover/popover-cell-renderer.component";
import { TooltipCellRendererComponent } from "./tooltip/tooltip-cell-renderer.component";
import _ from "lodash";
import { isNullOrUndefined } from "util";

import { CustomColDef } from "./custom-colDef";
@Component({
  selector: "bssh-data-table",
  templateUrl: "./bssh-data-table.component.html",
  styleUrls: [
    "./bssh-data-table.component.scss",
    "./radio-checkbox.component.scss",
  ],
  exportAs: "bsshDataTable",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BsshDataTableServerSideDatasourceService, BsshDataTableApiService],
})
export class BsshDataTableComponent implements OnInit, OnDestroy, AfterViewInit {
  public _requestOptions: IServerSideRequestOptions = {
    origin: "",
    path: "",
    params: {
      limit: 50,
      offset: 0,
    },
  };
  get requestOptions() {
    return this._requestOptions;
  }
  @Input("requestOptions")
  set requestOptions(data) {
    Object.assign(this._requestOptions, data);
  }

  @Input() public gridOptions: GridOptions | any;
  @Input() public selectedRows: any[] = [];
  @Input() public rowData = [];
  @Input() public showNativePagination: boolean = false;
  @Input() public useClassForChooser: boolean = false;
  @Input() public isSearchApiData: boolean = false;

  @Output() public selectionChanged: EventEmitter<GridApi> =
    new EventEmitter<GridApi>();
  @Output() public sortingChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() public columnVisibilityChanged: EventEmitter<any> =
    new EventEmitter<any>();
  @Output() public filteringChanged: EventEmitter<any> =
    new EventEmitter<any>();
  @Output() public filterModified: EventEmitter<any> = new EventEmitter<any>();
  @Output() public rowDataChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() public rowDataUpdated: EventEmitter<any> = new EventEmitter<any>();
  @Output() public pageChanged: EventEmitter<GridApi> =
    new EventEmitter<GridApi>();
  @Output() public gridReady: EventEmitter<GridApi> =
    new EventEmitter<GridApi>();
  @Output() public columnResized: EventEmitter<ColumnResizedEvent> =
    new EventEmitter<ColumnResizedEvent>();
  @Output() public pageSizeChanged: EventEmitter<number> =
    new EventEmitter<number>();

  @Output() public onClearSelected: EventEmitter<boolean> = new EventEmitter();

  @ViewChild(PaginationComponent, { static: true })
  libPagination: PaginationComponent;

  @ViewChild("pageSizeController", { static: false })
  pageSizeController: CustomPageSizeControllerComponent;

  public frameworkComponents: any;
  public icons: any;
  public serverSide: boolean;
  public defaultColDef: any;

  /**
   * used in serverside case only
   * gets updated based on whether 'select-all-current-page' class is passed onto the first column's definition
   */
  public selectAllIsEnabled: boolean;

  @ViewChild("gridRef", { static: false })
  public gridRef: ElementRef;

  // ────────────────────────────────────────────────────────────────────────────────
  // Column switcher options
  @Input() showColumnsSwitcher: "enable" | "disable";
  @Input() columnSwitcherTitle: string = "columns";
  @Input() columnSwitcherSelectAllTitle: string = "Select All";
  // ────────────────────────────────────────────────────────────────────────────────
  @Input() showPageSizeController: "enable" | "disable" = "disable";
  @Input() showPageSizeChangeController: "enable" | "disable" = "enable";
  @Input() enableColumnResizing: "enable" | "disable" = "enable";

  @HostListener("window:resize")
  onResize() {
    this.debounceResize();
  }

  public api: GridApi;
  public subs = new SubSink();
  public selectAllRefs: any;
  public selectAllEventHandler = this.handlerSelection.bind(this); // used only in case of 'serverside' row model type
  public readonly checkboxCheckedClass = "checkbox__label--checked";
  public readonly serverSideSelectAllHeaderClass = "select-all-current-page";

  public tableIsLoading: boolean = false;

  constructor(
    public dataTableApiService: BsshDataTableApiService,
    public serverSideDatasourceService: BsshDataTableServerSideDatasourceService,
    public elementRef: ElementRef,
    public cdr: ChangeDetectorRef
  ) {
    const element = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    if (this.useClassForChooser) {
      this.elementRef.nativeElement.classList.add(`chooser-modal`);
    }

    // Overwrite default gridOptions with user's gridOption input
    this.gridOptions = {
      ...defaultGridOptions,
      ...this.gridOptions,
    };

    this.defaultColDef = {
      ...this.gridOptions.defaultColDef,
      resizable: this.enableColumnResizing === "enable" ? true : false,
    };
    if (this.gridOptions && this.defaultColDef.resizable === false) {
      (this.gridOptions as GridOptions).columnDefs.forEach((colDef) => {
        (colDef as CustomColDef).resizable = this.defaultColDef.resizable;
      });
    }

    if (!this.gridOptions.overlayLoadingTemplate) {
      // use default custom-loading-overlay component if user is not overriding the overlay template
      this.gridOptions.loadingOverlayComponent = "customLoadingOverlay";
    }

    if (
      Array.isArray(this.gridOptions.columnDefs) &&
      this.gridOptions.columnDefs.length
    ) {
      const columnDef: any =
        this.gridOptions.columnDefs[this.gridOptions.columnDefs.length - 1];
      const { headerClass, cellClass } = columnDef;

      columnDef.headerClass = headerClass ? `${headerClass} last` : "last";

      columnDef.cellClass = cellClass ? `${cellClass} last` : "last";
    }

    this.frameworkComponents = {
      headerWithSortingFilter: CustomHeaderComponent,
      customFilter: CustomFilterComponent,
      customLoadingOverlay: CustomLoadingOverlayComponent,
      customLoadingCellRenderer: CustomCellLoadingRendererComponent,
      span: SpanCellRendererComponent,
      link: LinkCellRendererComponent,
      popover: PopoverCellRendererComponent,
      tooltip: TooltipCellRendererComponent,
    };
    const prefix =
      this.gridOptions.rowSelection === "single" ? "radio" : "checkbox";

    this.icons = {
      checkboxChecked: `<span class="${prefix}__label ${prefix}__label--checked"></span>`,
      checkboxUnchecked: `<span class="${prefix}__label"></span>`,
      checkboxIndeterminate: `<span class="${prefix}__label"></span>`,
    };

    this.serverSide = this.gridOptions.rowModelType === "serverSide";

    // set selectAllIsEnabled to enable/disable our custom server side headerCheckbox select all solution
    if (this.serverSide) {
      const columnDefs = this.gridOptions.columnDefs;
      if (columnDefs && columnDefs.length > 0) {
        this.selectAllIsEnabled = columnDefs[0].headerClass
          ? (columnDefs[0].headerClass as Array<string>).includes(
              this.serverSideSelectAllHeaderClass
            )
          : false;
      }
    }
    if (
      this.gridOptions.rowModelType === Constants.ROW_MODEL_TYPE_CLIENT_SIDE
    ) {
      this.tableIsLoading = false;
    }
  }

  ngAfterViewInit() {
    if (this.serverSide) {
      this.subs.sink = this.dataTableApiService.dataChanged.subscribe(
        (data) => {
          if (this.api && data) {
            this.libPagination.totalPages = this.totalPages;

            this.libPagination.marcForCheck();
          }
        }
      );

      // Need to manage server table header checkbox selection (https://stackoverflow.com/a/57891423)
      this.selectAllRefs = this.elementRef.nativeElement.querySelectorAll(
        "." + this.serverSideSelectAllHeaderClass + " .ag-input-wrapper"
      );
      this.selectAllRefs.forEach((element) => {
        element.addEventListener("click", this.selectAllEventHandler, false);
      });
    }

    if (this.showNativePagination) {
      setTimeout(() => {
        const element = document.querySelector(".ag-paging-panel");
        if (element === null) {
          return;
        }
        element.setAttribute("style", "display: block");
      }, 0);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();

    if (this.selectAllRefs) {
      this.selectAllRefs.forEach((element) => {
        element.removeEventListener("click", this.selectAllEventHandler, false);
      });
    }
  }

  public postProcessPopup(params) {
    const boundaries =
      params.eventSource && params.eventSource.getBoundingClientRect();
    if (boundaries && params.ePopup) {
      params.ePopup.style.width = `${boundaries.width}px`;
    }
  }

  public setColumns(updateData: { colId: any; state: boolean }[]) {
    // Defense-in-depth: never hide a required column even if a switcher emits one.
    const filteredUpdate = (updateData || []).filter(
      (update) => !this.isRequiredColId(update.colId)
    );

    // updateData only when columnApi is ready
    if (this.gridOptions.columnApi) {
      filteredUpdate.forEach((update) => {
        (this.gridOptions as any).columnApi.setColumnVisible(
          update.colId,
          update.state
        );
      });
    }

    this.columnVisibilityChanged.emit(filteredUpdate);
    this.debounceResize();
  }

  private isRequiredColId(colId: any): boolean {
    if (
      !this.gridOptions ||
      !Array.isArray(this.gridOptions.columnDefs) ||
      colId === undefined ||
      colId === null
    ) {
      return false;
    }
    const col = (this.gridOptions.columnDefs as CustomColDef[]).find(
      (c) => c.colId === colId || c.field === colId
    );
    return !!(col && col.showAlways === true);
  }

  public setNewSelectAllCheckbox() {
    const displayedColumns =
      this.gridOptions != null && this.gridOptions.columnApi != null
        ? this.gridOptions.columnApi.getAllDisplayedColumns()
        : [];

    if (displayedColumns.length === 0) {
      return;
    }

    const firstDisplayedColumn = displayedColumns[0];
    const columnName = firstDisplayedColumn.colDef.headerName
      ? firstDisplayedColumn.colDef.headerName
      : firstDisplayedColumn.colDef.field;

    // To get the index of rendered <div> that represents first displayed column
    const headers = this.elementRef.nativeElement.querySelectorAll(
      ".ag-header-row .ag-header-cell .ag-header-cell-text"
    );
    const list = [...(headers as HTMLElement[])];
    const index = list.findIndex(
      (element) => element.innerText.toUpperCase() === columnName.toUpperCase()
    );

    // Removing the previous select-all box and the event listener attached to it
    if (this.selectAllRefs) {
      this.selectAllRefs.forEach((element) => {
        element.removeEventListener("click", this.selectAllEventHandler, false);
      });
    }

    const headerDivs = this.elementRef.nativeElement.querySelectorAll(
      ".ag-header-row .ag-header-cell"
    );
    (headerDivs as HTMLElement[]).forEach((element: HTMLElement) => {
      const isOldSelectAllCheckbox = element.classList.contains(
        this.serverSideSelectAllHeaderClass
      );
      if (isOldSelectAllCheckbox) {
        element.classList.remove(this.serverSideSelectAllHeaderClass);
      }
    });

    // add class to new first column header to override the css and display the hidden checkbox in serverside modeltype
    if (
      firstDisplayedColumn.colDef.headerCheckboxSelection !== false ||
      firstDisplayedColumn.colDef.checkboxSelection !== false
    ) {
      // Shouldn't add css class to display hidden checkbox when both row selection and header selection are disabled
      // It will cause misalignment for header and row content
      (headerDivs[index] as HTMLElement).classList.add(
        "select-all-current-page"
      );
    }

    // add click event listener to new first column checkbox for selecting all rows
    this.selectAllRefs = this.elementRef.nativeElement.querySelectorAll(
      "." + this.serverSideSelectAllHeaderClass + " .ag-input-wrapper"
    );
    this.selectAllRefs.forEach((element) => {
      element.addEventListener("click", this.selectAllEventHandler, false);
    });
  }

  public onGridReady({ api }: GridReadyEvent): void {
    this.api = api;
    this.debounceResize();

    if (this.serverSide) {
      this.serverSideDatasourceService.init({
        apiDriver: this.dataTableApiService,
        gridInstance: this.api,
        requestOptions: this.requestOptions,
        gridOptions: this.gridOptions,
      });

      // Show loading api overlay (Server table only)
      this.subs.sink = this.dataTableApiService.dataLoadingStatus$.subscribe(
        (status) => {
          if (status === DataLoadingStatus.RowsLoaded) {
            this.api.hideOverlay();
          } else if (status === DataLoadingStatus.NoRows) {
            this.api.showNoRowsOverlay();
          }
          if (status !== DataLoadingStatus.Loading) {
            this.tableIsLoading = false;
            this.initializePageSizeController();
          }
        }
      );
    } else if (
      this.gridOptions.rowModelType === Constants.ROW_MODEL_TYPE_CLIENT_SIDE
    ) {
      this.initializePageSizeController();
    }
    this.updatePaginationParams();
    this.gridReady.emit(this.api);
  }

  public onDisplayedColumnsChanged($event) {
    // headercheckbox selection is not supported in serverside model of ag-grid
    // so to handle that thing....when there is new header in leftmost column

    // this should work only in case of serverside
    if (!this.serverSide) {
      return;
    }

    // should not work if rowSelection is 'single' type
    // And should also not work if select all solution is not enabled (need to pass 'select-all-current-page' headerClass
    // to first column to enable)
    if (
      this.gridOptions.rowSelection === "single" ||
      !this.selectAllIsEnabled
    ) {
      return;
    }

    this.setNewSelectAllCheckbox();
  }

  /**
   * Resize need to be rebounced
   */
  public debounceResize = debounce(() => {
    // only call sizeColumnToFit when api is ready
    if (this.api) {
      this.api.sizeColumnsToFit();
    }
  }, 40);

  /**
   * When server table header checkbox (select/deselect-all rows) is checked,
   * selectionChanged event will be emitted for each row. This method debounces
   * the multiple events. Also in server-side mode, we will have to manage header
   * checkbox updates.
   */
  // tslint:disable-next-line: member-ordering
  public debouncedSelectionChanged = debounce(($event) => {
    this.selectionChanged.emit($event);
    this.selectedRows = this.api.getSelectedRows();
    this.setSelectAllCheckboxStatus();
  }, 40);

  public onSelectionChanged($event): void {
    this.selectionChanged.emit($event);
    this.selectedRows = this.api.getSelectedRows();
  }

  public onColumnResize($event: ColumnResizedEvent): void {
    // Only interested in the column resizing that user Initiated.
    // Grid Size to fit calls also trigger this event, but filter those out.
    if ($event && $event.source === "uiColumnDragged" && $event.finished) {
      this.columnResized.emit($event);
    }
  }

  public onPageChanged(page) {
    this.api.paginationGoToPage(page - 1);
    this.updatePaginationParams();

    this.pageChanged.emit(this.api);

    // Update page size controller status
    if (this.showPageSizeController && this.pageSizeController) {
      this.pageSizeController.updatePageStatus();
    }

    if (this.serverSide) {
      this.setSelectedRows();
      this.setSelectAllCheckboxStatus();
    }
  }

  public onPaginationChanged() {
    this.updatePaginationParams();
  }

  public updatePaginationParams() {
    if (!this.api) {
      return;
    }

    this.currentPage = this.api.paginationGetCurrentPage() + 1;
    this.totalPages = this.api.paginationGetTotalPages();
  }

  public _currentPage = 1;
  set currentPage(value) {
    this._currentPage = value;
  }
  get currentPage() {
    return this.api
      ? this.api.paginationGetCurrentPage() + 1
      : this._currentPage;
  }

  public setSelectAllCheckboxStatus() {
    // Update header checkbox to reflect 'select-all' state or not
    const renderedNodes = this.api.getRenderedNodes();
    // when single select option is specified, there is no 'Select All' check box.
    if (this.selectAllRefs[0] != null) {
      const headerCheckboxRef =
        this.selectAllRefs[0].querySelector(".checkbox__label");
      if (headerCheckboxRef) {
        const headerCheckboxClassList = headerCheckboxRef.classList;

        const areAllRenderedRowsSelected = renderedNodes.every(
          (renderedNode: RowNode) => {
            return this.selectedRows.find((selectedRow: any) => {
              return selectedRow === renderedNode.data;
            });
          }
        );

        if (areAllRenderedRowsSelected) {
          headerCheckboxClassList.add(this.checkboxCheckedClass);
        } else if (
          headerCheckboxClassList.contains(this.checkboxCheckedClass)
        ) {
          headerCheckboxClassList.remove(this.checkboxCheckedClass);
        }
      }
    }
  }
  // set selected rows when page loads
  public setSelectedRows() {
    if (isNullOrUndefined(this.selectedRows)) {
      this.api.forEachNode((node) => node.setSelected(false));
    } else {
      const renderedNodes = this.api.getRenderedNodes();
      _.each(renderedNodes, (node) => {
        if (_.find(this.selectedRows, (selected) => node.data === selected)) {
          node.setSelected(true);
        }
      });
    }
  }

  public _totalPages = 1;
  set totalPages(value) {
    this._totalPages = value;
  }
  get totalPages() {
    return this.api ? this.api.paginationGetTotalPages() : this._totalPages;
  }

  /**
   * Forces the server table to make the api call to fetch data rows.
   * This is useful for reloading the runs list when user-context changes,
   * where the api url still remains the same.
   */
  public reloadServerTable(): void {
    if (!this.serverSide) {
      console.error(
        "reloadServerTable() does not work when gridOption does not have `rowModelType: 'serverSide'`"
      );
      return;
    }
    this.api.purgeServerSideCache([]);
    // Reset rows selected (uncheck all rows)
    this.api.deselectAll();
  }

  public clearSelected(): void {
    this.api.deselectAll();
    this.onClearSelected.emit(true);
  }

  public onSortChanged($event): void {
    this.sortingChanged.emit($event);
    // Reset rows selected (uncheck all rows)
    this.api.deselectAll();
  }

  public onFilterChanged($event): void {
    if ($event.sorting) {
      delete $event.sorting;
      $event.type = "sortChanged";
      this.sortingChanged.emit($event);
    } else {
      this.filteringChanged.emit($event);
    }
    // Reset rows selected (uncheck all rows)
    this.api.deselectAll();
  }

  public onFilterModified($event): void {
    this.filterModified.emit($event);
    // Reset rows selected (uncheck all rows)
    this.api.deselectAll();
  }

  public onRowDataChanged($event): void {
    this.rowDataChanged.emit($event);
  }

  public onRowDataUpdated($event): void {
    this.rowDataUpdated.emit($event);
  }

  // selectAll selection handler
  // Only used in server-table, since header checkbox selection for server side is not managed by ag-grid.
  public handlerSelection($event) {
    const nodesRefs = this.api.getRenderedNodes();

    if ($event.target.classList.contains(this.checkboxCheckedClass)) {
      nodesRefs.forEach((node) => node.setSelected(false));
    } else {
      nodesRefs.forEach((node) => node.setSelected(true));
    }
  }

  public initializePageSizeController() {
    if (this.showPageSizeController && this.pageSizeController) {
      this.pageSizeController.gridReady(this.api);
    }
  }

  public onPageSizeChanged(pageSize: number): void {
    this.pageSizeChanged.emit(pageSize);
  }
}

export { GridOptions } from "ag-grid-community";
